import { useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { assetsApi, type Asset, type PaginatedAssets } from '../services/assetsApi';
import { queryKeys } from '@/lib/queryKeys';

interface UseAssetsPaginatedParams {
  source?: 'user' | 'printtat';
  category?: string;
  search?: string;
}

export const useAssetsPaginated = (params: UseAssetsPaginatedParams) => {
  const queryClient = useQueryClient();
  const filters = { source: params.source, category: params.category, search: params.search };

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedAssets>({
    queryKey: queryKeys.assets.list(filters),
    queryFn: ({ pageParam }) =>
      assetsApi.listPaginated({ ...params, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    staleTime: 30_000,
  });

  const assets = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data],
  );

  const total = data?.pages[data.pages.length - 1]?.meta.total ?? 0;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.assets.lists() });

  const addAsset = (asset: Asset) => {
    queryClient.setQueryData<{ pages: PaginatedAssets[]; pageParams: number[] }>(
      queryKeys.assets.list(filters),
      (old) => {
        if (!old) return old;
        const pages = [...old.pages];
        pages[0] = {
          ...pages[0],
          data: [asset, ...pages[0].data],
          meta: { ...pages[0].meta, total: pages[0].meta.total + 1 },
        };
        return { ...old, pages };
      },
    );
    invalidate();
  };

  const removeAsset = (id: string) => {
    queryClient.setQueryData<{ pages: PaginatedAssets[]; pageParams: number[] }>(
      queryKeys.assets.list(filters),
      (old) => {
        if (!old) return old;
        const pages = old.pages.map((p) => ({
          ...p,
          data: p.data.filter((a) => a.id !== id),
          meta: { ...p.meta, total: p.meta.total - 1 },
        }));
        return { ...old, pages };
      },
    );
    invalidate();
  };

  const updateAsset = (id: string, updates: Partial<Asset>) => {
    queryClient.setQueryData<{ pages: PaginatedAssets[]; pageParams: number[] }>(
      queryKeys.assets.list(filters),
      (old) => {
        if (!old) return old;
        const pages = old.pages.map((p) => ({
          ...p,
          data: p.data.map((a) => (a.id === id ? { ...a, ...updates } : a)),
        }));
        return { ...old, pages };
      },
    );
    invalidate();
  };

  return {
    assets,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    addAsset,
    removeAsset,
    updateAsset,
  };
};
